import throttle from 'lodash/throttle';
import React from 'react';
import { scrollIntoView } from 'seamless-scroll-polyfill';
import { MESSAGE_TYPE_LOCAL, MESSAGE_TYPE_REMOTE } from '../../constants';
import AbstractMessageContainer from '../AbstractMessageContainer';
import { getRemoteParticipants, getUserIdByOccupantId } from '../../../base/participants';
import { setRecentSenders } from '../../../vtchat-moderation/slice';
import { setHasNewMessage, setLastReadMessageId } from '../../actions.any';
import { getHasNewMessage, getLastReadMessageId } from '../../functions';
import ChatMessageGroup from './ChatMessageGroup';
import NewMessagesButton from './NewMessagesButton';
import ChatImagesViewer from './ChatImagesViewer';
import VirtualList from './VirtualList';
/**
 * Displays all received chat messages, grouped by sender.
 *
 * @augments AbstractMessageContainer
 */
export default class MessageContainer extends AbstractMessageContainer {
    constructor(props) {
        super(props);
        /**
         * Component state used to decide when the hasNewMessages button to appear
         * and where to scroll when click on hasNewMessages button.
         */
        this.state = {
            // hasNewMessages: false,
            isScrolledToBottom: false,
            // lastReadMessageId: '',
            hasVerticalScrollbar: false,
            countNumber: 0
        };
        this.intervalId = null;
        this.timeoutId = null;
        this._messageListRef = React.createRef();
        this._messagesListEndRef = React.createRef();
        // Bind event handlers so they are only bound once for every instance.
        this._handleIntersectBottomList = this._handleIntersectBottomList.bind(this);
        this._findFirstUnreadMessage = this._findFirstUnreadMessage.bind(this);
        this._isMessageVisible = this._isMessageVisible.bind(this);
        this._onChatScroll = throttle(this._onChatScroll.bind(this), 300, { leading: true });
        this._onRecountChat = this._onRecountChat.bind(this);
        this._onGoToFirstUnreadMessage = this._onGoToFirstUnreadMessage.bind(this);
    }
    /**
     * Implements {@code Component#render}.
     *
     * @inheritdoc
     */
    render() {
        const groupedMessages = this._getMessagesGroupedBySenderAndTime();
        const sendersInfor = {};
        groupedMessages.forEach(group => {
            const latest = group[group.length - 1];
            const senderInfo = {
                senderAccId: latest.senderAccId,
                senderAccName: latest.senderAccName,
                senderAvatar: latest.senderAvatar
            };
            sendersInfor[latest.senderAccId] = senderInfo;
        });
        const participants = getRemoteParticipants(APP.store.getState());
        participants.forEach((p, id) => {
            const userId = getUserIdByOccupantId(APP.store.getState(), id);
            if (userId) {
                if (userId
                    && (!sendersInfor[userId]
                        || (sendersInfor[userId]?.senderAccName !== p?.name
                            || sendersInfor[userId]?.senderAvatar !== p?.avatarURL))) {
                    sendersInfor[userId] = {
                        senderAccId: userId,
                        senderAccName: p?.name,
                        senderAvatar: p?.avatarURL
                    };
                }
            }
        });
        APP.store.dispatch(setRecentSenders(sendersInfor));
        if (this.props.pendingMessage) {
            groupedMessages.push([{
                    displayName: "pending",
                    hasRead: true,
                    id: "pendingMessage",
                    isReaction: false,
                    message: this.props.pendingMessage.message,
                    messageType: "local",
                    isPendingMessage: true,
                    recipient: "pending",
                    lobbyChat: false,
                    messageId: "pendingMessage",
                    participantId: "pendingMessage",
                    privateMessage: false,
                    timestamp: Date.now(),
                    senderAccId: "pendingMessage",
                    attachments: this.props.pendingMessage.files?.map(pmf => ({ ...this.props.files?.find(f => f.id === pmf.id), isPending: true, id: pmf.id || '' }))
                }]);
        }
        const messages = groupedMessages.map((group, index) => {
            const messageType = group[0]?.messageType;
            return (React.createElement(ChatMessageGroup, { className: messageType || MESSAGE_TYPE_REMOTE, key: index, messages: group, dispatch: APP.store.dispatch }));
        });
        const renderList = (item, index) => {
            const messageType = groupedMessages[index][0]?.messageType;
            return (React.createElement(ChatMessageGroup, { className: messageType || MESSAGE_TYPE_REMOTE, key: index, messages: groupedMessages[index], dispatch: APP.store.dispatch }));
        };
        const handleScrollUp = () => {
            if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
            }
        };
        return (React.createElement("div", { id: 'chat-conversation-container' },
            React.createElement("div", { "aria-labelledby": 'chat-header', id: 'chatconversation', className: `${this.state.hasVerticalScrollbar ? 'have-scroll' : ''} ${this.props.messages?.length ? '' : 'empty'}`, onScroll: this._onChatScroll, ref: this._messageListRef, role: 'log', tabIndex: 0 },
                React.createElement(VirtualList, { id: "vitural-message-list", stickyToBottom: true, items: groupedMessages, height: 530, estimatedItemHeight: 63, renderItem: (item, index) => renderList(item, index), _messagesListEndRef: this._messagesListEndRef, style: { display: "flex", flexDirection: "column" }, scrollUp: handleScrollUp }),
                React.createElement(NewMessagesButton, { display: !this.state.isScrolledToBottom && getHasNewMessage(APP.store.getState()), onGoToFirstUnreadMessage: this._onGoToFirstUnreadMessage }),
                React.createElement(ChatImagesViewer, { messages: this.props.messages }))));
    }
    /**
     * Implements {@code Component#componentDidMount}.
     * When Component mount scroll message container to bottom.
     * Create observer to react when scroll position is at bottom or leave the bottom.
     *
     * @inheritdoc
     */
    componentDidMount() {
        if (getHasNewMessage(APP.store.getState())) {
            this._onGoToFirstUnreadMessage();
        }
        else {
            this.scrollToElement(false, null);
        }
        this._createBottomListObserver();
        this.setState({ hasVerticalScrollbar: this.props.messages?.length > 16 });
    }
    /**
     * Implements {@code Component#componentDidUpdate}.
     * If the user receive a new message or the local user send a new message,
     * scroll automatically to the bottom if scroll position was at the bottom.
     * Otherwise update hasNewMessages from component state.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidUpdate(prevProps) {
        const newMessages = this.props.messages.filter(message => !prevProps.messages.includes(message));
        const hasLocalMessage = newMessages.map(message => message.messageType).includes(MESSAGE_TYPE_LOCAL);
        if (newMessages.length > 0) {
            if (this.state.isScrolledToBottom || hasLocalMessage) {
                this.scrollToElement(false, null);
                this._onRecountChat();
            }
            else {
                // eslint-disable-next-line react/no-did-update-set-state
                // this.setState({ hasNewMessages: true });
                APP.store.dispatch(setHasNewMessage(true));
            }
            if (this._messageListRef && this._messageListRef?.current) {
                this.setState({
                    hasVerticalScrollbar: this._messageListRef?.current?.scrollHeight > this._messageListRef?.current?.clientHeight
                });
            }
        }
    }
    /**
     * Implements React's {@link Component#componentWillUnmount()}. Invoked
     * immediately before this component is unmounted and destroyed.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        const target = document.querySelector('#messagesListEnd');
        // this._bottomListObserver.unobserve(target as Element);
    }
    /**
     * Automatically scrolls the displayed chat messages to bottom or to a specific element if it is provided.
     *
     * @param {boolean} withAnimation - Whether or not to show a scrolling.
     * @param {TMLElement} element - Where to scroll.
     * Animation.
     * @returns {void}
     */
    scrollToElement(withAnimation, element, timeInterval) {
        const scrollTo = element ? element : this._messagesListEndRef.current;
        const block = element ? 'center' : 'nearest';
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        if (!this.intervalId) {
            this.intervalId = setInterval(() => {
                scrollIntoView(scrollTo, {
                    behavior: withAnimation ? 'smooth' : 'auto',
                    block
                });
                this.setState({
                    countNumber: this.state.countNumber + 1,
                });
                if (this.state.isScrolledToBottom && this.intervalId) {
                    clearInterval(this.intervalId);
                    this.intervalId = null;
                }
            }, timeInterval || 100);
        }
    }
    _onRecountChat() {
        const firstUnreadMessage = this._findFirstUnreadMessage(true);
        return firstUnreadMessage;
    }
    /**
     * Callback invoked to listen to current scroll position and update next unread message.
     * The callback is invoked inside a throttle with 300 ms to decrease the number of function calls.
     *
     * @private
     * @returns {void}
     */
    _onChatScroll() {
        const firstUnreadMessage = this._findFirstUnreadMessage();
        if (firstUnreadMessage && firstUnreadMessage.id !== getLastReadMessageId(APP.store.getState())) {
            APP.store.dispatch(setLastReadMessageId(firstUnreadMessage?.id));
        }
    }
    /**
     * Find the first unread message.
     * Update component state and scroll to element.
     *
     * @private
     * @returns {void}
     */
    _onGoToFirstUnreadMessage() {
        const firstUnreadMessage = this._findFirstUnreadMessage();
        APP.store.dispatch(setLastReadMessageId(firstUnreadMessage?.id || ''));
        this.scrollToElement(true, firstUnreadMessage, 300);
    }
    /**
    * Create observer to react when scroll position is at bottom or leave the bottom.
    *
    * @private
    * @returns {void}
    */
    _createBottomListObserver() {
        // const options = {
        //     root: document.querySelector('#chatconversation'),
        //     rootMargin: '35px',
        //     threshold: 0.5
        // };
        // const target = document.querySelector('#messagesListEnd');
        // if (target) {
        //     this._bottomListObserver = new IntersectionObserver(this._handleIntersectBottomList, options);
        //     this._bottomListObserver.observe(target);
        // }
        const container = document.querySelector('#vitural-message-list');
        if (!container)
            return;
        // Remove old handler if exists
        if (this._onScrollHandler) {
            container.removeEventListener('scroll', this._onScrollHandler);
        }
        // ✅ Define the scroll handler function here
        this._onScrollHandler = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const distanceToBottom = scrollHeight - (scrollTop + clientHeight);
            const isAtBottom = distanceToBottom <= 35;
            if (isAtBottom && this.props.messages.length) {
                const lastMessageIndex = this.props.messages.length - 1;
                const lastMessage = this.props.messages[lastMessageIndex];
                const lastReadMessageId = lastMessage.messageId;
                APP.store.dispatch(setLastReadMessageId(lastReadMessageId));
                APP.store.dispatch(setHasNewMessage(false));
                if (this.timeoutId) {
                    clearTimeout(this.timeoutId);
                }
                this.timeoutId = setTimeout(() => {
                    this.setState({
                        isScrolledToBottom: true,
                    });
                }, 500);
            }
            else {
                if (this.timeoutId) {
                    clearTimeout(this.timeoutId);
                }
                this.timeoutId = setTimeout(() => {
                    this.setState({
                        isScrolledToBottom: false,
                    });
                }, 500);
            }
        };
        // Attach scroll listener
        container.addEventListener('scroll', this._onScrollHandler);
    }
    /** .
    * _HandleIntersectBottomList.
    * When entry is intersecting with bottom of container set last message as last read message.
    * When entry is not intersecting update only isScrolledToBottom with false value.
    *
    * @param {Array} entries - List of entries.
    * @private
    * @returns {void}
    */
    _handleIntersectBottomList(entries) {
        entries.forEach((entry) => {
            if (entry.isIntersecting && this.props.messages.length) {
                const lastMessageIndex = this.props.messages.length - 1;
                const lastMessage = this.props.messages[lastMessageIndex];
                const lastReadMessageId = lastMessage.messageId;
                APP.store.dispatch(setLastReadMessageId(lastReadMessageId));
                APP.store.dispatch(setHasNewMessage(false));
                this.setState({
                    isScrolledToBottom: true,
                    // hasNewMessages: false,
                    // lastReadMessageId
                });
            }
            if (!entry.isIntersecting) {
                this.setState({
                    isScrolledToBottom: false
                });
            }
        });
    }
    /**
    * Find first unread message.
    * MessageIsAfterLastSeenMessage filter elements which are not visible but are before the last read message.
    *
    * @private
    * @returns {Element}
    */
    _findFirstUnreadMessage(resetLastRead = false) {
        const messagesNodeList = document.querySelectorAll('.chatmessage-wrapper-container');
        // @ts-ignore
        const messagesToArray = [...messagesNodeList];
        const previousIndex = messagesToArray.findIndex((message) => message.id === getLastReadMessageId(APP.store.getState()));
        if (previousIndex !== -1) {
            for (let i = previousIndex; i < messagesToArray.length; i++) {
                if (!this._isMessageVisible(messagesToArray[i])) {
                    return messagesToArray[i];
                }
                else if (resetLastRead && messagesToArray[i]?.id) {
                    APP.store.dispatch(setLastReadMessageId(messagesToArray[i]?.id));
                }
            }
        }
    }
    /**
     * Check if a message is visible in view.
     *
     * @param {Element} message - The message.
     *
     * @returns {boolean}
     */
    _isMessageVisible(message) {
        const { bottom, height, top } = message.getBoundingClientRect();
        if (this._messageListRef.current) {
            const containerRect = this._messageListRef.current.getBoundingClientRect();
            return top <= containerRect.top
                ? containerRect.top - top <= height : bottom - containerRect.bottom <= height;
        }
        return false;
    }
}
